import { useEffect, useCallback } from 'react';
import { useChatStore } from '../stores/chatStore';

/**
 * Hook to handle ending the session when the page is unloaded.
 * Implements multiple redundant approaches to ensure the session is properly ended.
 */
export function usePageUnloadHandler() {
  const { sessionId, threadId, endSession, isSessionEnding, actualMerchantId } = useChatStore();

  const sendBeaconToServer = useCallback(() => {
    // Only send if we have active session data
    if (!sessionId || !threadId || !actualMerchantId) {
      console.log('[MattressAI] No active session, skipping end beacon');
      return false;
    }

    try {
      const data = new FormData();
      data.append('sessionId', sessionId);
      data.append('threadId', threadId);
      data.append('merchantId', actualMerchantId);
      data.append('endReason', 'inactive');

      // Use absolute URL for beacon to ensure it goes to the right endpoint
      const beaconEndpoint = 'https://api.mattressai.co/api/chat/sessions/end-beacon';
      
      // Send beacon to API endpoint
      const beaconResult = navigator.sendBeacon(beaconEndpoint, data);
      
      console.log('[MattressAI] Beacon send result:', beaconResult);

      // Store the pending end in local storage as backup
      try {
        localStorage.setItem('session_end_pending', JSON.stringify({
          sessionId,
          threadId,
          merchantId: actualMerchantId,
          timestamp: Date.now()
        }));
      } catch (storageError) {
        console.warn('[MattressAI] Failed to store session end in localStorage', storageError);
      }

      return beaconResult;
    } catch (error) {
      console.error('[MattressAI] Error sending beacon', error);
      return false;
    }
  }, [sessionId, threadId, actualMerchantId]);

  useEffect(() => {
    // If no session, don't set up handlers
    if (!sessionId || !threadId) return;
    
    // Attempt to use the most reliable method first - sendBeacon
    // This has the best chance of completing before the page is unloaded
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't do anything if we're already ending the session
      if (isSessionEnding) return;
      
      try {
        console.log('[MattressAI] Page unloading, ending session via beacon...');
        
        // Get the merchant ID from the store state
        const merchantId = useChatStore.getState().actualMerchantId || 
          localStorage.getItem('mattressai-merchant-id') || 
          'unknown';
          
        // Create session end data
        const endData = new Blob([JSON.stringify({
          sessionId,
          threadId,
          merchantId,
          lastActivityTime: new Date().toISOString(),
          completionReason: 'page_unload'
        })], { type: 'application/json' });
        
        // Try to use navigator.sendBeacon with absolute URL to API endpoint
        const beaconSuccess = navigator.sendBeacon(
          'https://api.mattressai.co/api/chat/sessions/end-beacon', 
          endData
        );
        
        console.log('[MattressAI] Beacon send result:', beaconSuccess);
        
        // Create a fallback cookie in case the beacon fails
        // This can be detected on the next page load
        try {
          localStorage.setItem('session_end_pending', JSON.stringify({
            sessionId,
            threadId,
            merchantId,
            timestamp: Date.now()
          }));
        } catch (storageError) {
          console.error('[MattressAI] Failed to store session end fallback:', storageError);
        }
      } catch (error) {
        console.error('[MattressAI] Failed to end session on page unload:', error);
      }
    };

    // Handle page visibility changes as another chance to end the session
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !isSessionEnding) {
        console.log('[MattressAI] Page hidden, ending session...');
        endSession('page_hidden');
      }
    };

    // Attach our listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionId, threadId, endSession, isSessionEnding]);
} 